import { LearnArticle } from "@/types/learn";

/**
 * Curated fallback list of Learn articles used when the database is empty
 * (e.g., before the seed migration has been applied to a given environment).
 *
 * The DB copy in `learn_articles` is authoritative — when present, it wins
 * via `LearnService.list()` / `LearnService.getBySlug()`. This registry is
 * the offline-safe mirror so the Learn Hub is never empty.
 *
 * Source of truth for the article content is scripts/learn/seed-library.mjs —
 * keep this file in sync with that one.
 */
export const LEARN_ARTICLE_REGISTRY: LearnArticle[] = [
  {
    id: "static-welcome",
    slug: "welcome-to-help-genie",
    title: "Welcome to Help Genie",
    category: "getting-started",
    summary: "A 90-second orientation to what Help Genie does and where to start.",
    video_url: null,
    est_minutes: 2,
    order_index: 1,
    is_start_here: true,
    body_markdown: `## What Help Genie does

Help Genie lets you build voice and chat assistants — your **Genies** — for your business. A Genie can answer questions, capture leads, take after-hours calls, and surface insights from every conversation it has.

## Your starter path

A short series of reads will take you from a brand new account to a published, working Genie:

- **Set up your first Genie** — a guided voice interview, about three minutes.
- **Add knowledge** — PDFs, web pages, or pasted text the Genie can answer from.
- **Pick a voice** — explore the voice library to find the right tone for your brand.
- **Publish via a Portal** — get a shareable, branded page for your Genie.
- **Read your conversations** — every chat and call is logged with insights.

Each is its own short read. Hit **Mark complete and continue** at the bottom of each one to move to the next.

## Getting help anywhere

The **Your Genie** button in the sidebar opens an in-app helper at any time. Ask it anything about Help Genie and it will guide you. You can also open the dedicated Tutor page from the **Learn → Ask the Tutor** tab for a full-screen conversation.`,
  },
  {
    id: "static-choose-use-case",
    slug: "choose-your-genie-use-case",
    title: "Choose the right use case for your Genie",
    category: "getting-started",
    summary: "Pick the use case that matches the job your Genie will do.",
    video_url: null,
    est_minutes: 3,
    order_index: 2,
    is_start_here: false,
    body_markdown: `## Why this matters

Use case is the single biggest decision you make during Genie setup. It seeds the prompt defaults, the recommended voice tone, and the way your Genie handles silence and follow-ups. Picking the right one means less prompt tweaking later.

## The four common starters

- **Support** — answers questions, troubleshoots, escalates. Calmer voice, longer max duration. Pair with a Knowledge Base.
- **Lead capture** — qualifies interest, collects contact details, books follow-ups. Captures leads on by default, shorter max duration, friendly voice.
- **Sales** — explains the product, handles objections, books demos. Higher temperature for natural conversation, energetic voice.
- **Booking** — collects time, name, and contact for an appointment. Crisp, focused, low temperature.

## Mixing or switching later

You can change the use case any time on a Genie's detail page — it only resets defaults, it never deletes your knowledge or settings. Most teams end up with two or three Genies, each focused on one job: a support Genie + a lead Genie is a common combination.

## If you're not sure

Pick **support** if you have an FAQ-heavy business, **lead capture** if you're driving paid traffic to a landing page, **sales** if your Genie should explain a product, and **booking** if calendar slots are the primary goal.`,
  },
  {
    id: "static-create-first",
    slug: "create-your-first-genie",
    title: "Set up your first Genie",
    category: "getting-started",
    summary: "Build, name, and configure your first Genie via the guided setup.",
    video_url: null,
    est_minutes: 4,
    order_index: 3,
    is_start_here: true,
    body_markdown: `## Starting the setup

Click **Set Up a Genie** in the left sidebar (under Dashboard). You arrive at the setup page where Help Genie interviews you to build the Genie.

## How the interview works

It is a short voice conversation — you can also type if you prefer. You will be asked things like:

- The name your business or team goes by.
- What you want the Genie to help with — support, lead capture, sales, bookings.
- The kind of questions it should expect to handle.
- How you want it to sound and feel.

When the interview wraps up, you land on a **Confirmation** view that summarises what was captured. Review it, edit any field you want to change, and click the create button at the bottom.

## What happens next

Help Genie provisions the Genie in the background. You will get a notification when it is ready, and it will appear in **My Genies** in the sidebar. From there you can open it, refine its prompt, attach knowledge, and publish it.

## Tips

- Speak naturally during the interview. The clearer you are about the use case, the better the defaults.
- You can always rename, rewrite the prompt, or change the voice later from the Genie's detail page.
- If you already have a strong system prompt, paste it in during the interview when prompted.`,
  },
  {
    id: "static-invite-team",
    slug: "invite-your-team",
    title: "Invite your team to Help Genie",
    category: "getting-started",
    summary: "Bring teammates into your account to share Genies, leads, and insights.",
    video_url: null,
    est_minutes: 3,
    order_index: 4,
    is_start_here: false,
    body_markdown: `## Where this lives

Open **Account Settings** from your profile menu at the bottom of the sidebar. The **Team** section lists current members and lets you invite new ones.

## Adding a team member

Click **Invite member**, enter their email, choose a role, and send. They get an email with a magic link to join your account. Until they accept, they appear as **Invited** in the team list.

## Roles

- **Owner** — full access including billing and team management.
- **Member** — can create and manage Genies, view conversations and leads. Can't change billing or remove the Owner.

## What team members share

Everyone on the team sees the same Genies, conversations, leads, and analytics. Branding and connections are account-wide too. Lead routing, however, can be set per-Genie — so you can route different Genies to different teammates.

## Removing access

Open the team list, click the menu next to a member, and remove them. They lose access immediately. Their authored content stays on the account.`,
  },
  {
    id: "static-add-knowledge",
    slug: "add-knowledge-to-your-genie",
    title: "Add knowledge to your Genie",
    category: "knowledge",
    summary: "Feed your Genie documents, web pages, and FAQs so it can answer accurately.",
    video_url: null,
    est_minutes: 4,
    order_index: 5,
    is_start_here: true,
    body_markdown: `## Where knowledge lives

Open **Knowledge Base** in the sidebar. You will see three tabs:

- **Upload New** — add new documents to your library.
- **Knowledge Base** — your library of uploaded documents, where you attach them to Genies.
- **Genie View** — a per-Genie view of what each Genie can use.

## Adding documents

In the **Upload New** tab you can:

- **Upload files** — drag-and-drop PDFs, Word docs, or text files.
- **Paste a URL** — point at a help-centre or product page and Help Genie will crawl and index it.
- **Paste text directly** — useful for short FAQs or quick reference notes.

## Attaching knowledge to a Genie

A document is in your library, but no Genie uses it until you attach it. Open the **Knowledge Base** tab, find the document, and toggle on the Genies that should use it. A Genie can use as many documents as you like.

## What makes knowledge effective

- Trim long PDFs — anything irrelevant adds noise.
- Use clear headings; Genies index document structure.
- One topic per document. Several small docs beats one giant doc.
- Re-upload after big edits so the index stays current.`,
  },
  {
    id: "static-website-knowledge",
    slug: "use-a-website-as-knowledge",
    title: "Use a website as knowledge",
    category: "knowledge",
    summary: "Point your Genie at an existing help centre, product, or pricing page.",
    video_url: null,
    est_minutes: 3,
    order_index: 6,
    is_start_here: false,
    body_markdown: `## Why this is useful

If you already have a help centre, product page, or pricing page that explains your business, you don't need to retype it as a document. Help Genie can crawl a URL and use the page as a knowledge source.

## How to add one

Open **Knowledge Base** in the sidebar and go to the **Upload New** tab. Paste a URL into the URL field and submit. Help Genie fetches the page, parses it, and adds it to your library.

## What works well as a URL source

- Help centre articles.
- Product pages with rich text descriptions.
- Pricing pages.
- About / company background pages.
- Blog posts with evergreen reference content.

## What doesn't

- Pages behind a login.
- Pages that load their content via JavaScript only (most modern web apps).
- Sites with very heavy ads or popups.
- News or social feeds that change daily.

## After it's added

Open the **Knowledge Base** tab and attach the URL document to any Genies that should use it. If the source page changes, re-trigger the fetch from the document's menu to pick up the new content.`,
  },
  {
    id: "static-knowledge-troubleshooting",
    slug: "knowledge-troubleshooting",
    title: "Why isn't my Genie using my knowledge?",
    category: "knowledge",
    summary: "Four things to check when your Genie ignores a document you've uploaded.",
    video_url: null,
    est_minutes: 3,
    order_index: 7,
    is_start_here: false,
    body_markdown: `## The four common causes

If your Genie answers questions wrong despite having the right document loaded, run through these in order.

## 1. The document isn't attached to the Genie

Adding a document to your library doesn't automatically wire it to every Genie. Open **Knowledge Base → Knowledge Base** tab, find the document, and confirm the Genie you're testing is toggled on.

## 2. The document isn't indexed yet

Brand new documents take a moment to index. Check the document's status in the Knowledge Base tab — if it says **Processing**, give it a minute and try again.

## 3. The content doesn't match the question

Genies match the user's question against indexed content. If a user asks "How much is shipping?" but your document says "Delivery fees are calculated at checkout", the Genie might not realise they're related. Add synonyms ("shipping = delivery") or restructure the doc with the user's words.

## 4. The system prompt is overriding the knowledge

If your prompt says "Always answer in your own words" or "Don't reference outside material", the Genie may ignore the document. Remove or soften those rules.

## Still stuck?

Open the Tutor in the sidebar and paste the question your Genie got wrong, alongside the relevant document text. The Tutor can usually spot the mismatch in seconds.`,
  },
  {
    id: "static-voice-playground",
    slug: "try-your-genie-in-the-voice-playground",
    title: "Find a voice in the Voice Playground",
    category: "genies",
    summary: "Explore the voice library and pick the right voice for your Genie.",
    video_url: null,
    est_minutes: 3,
    order_index: 8,
    is_start_here: true,
    body_markdown: `## What the Voice Playground is

The **Voice Playground** is your library for voices. It is where you find a tone, accent, and energy that fits your brand before assigning a voice to one of your Genies.

## The tabs

- **Recommended** — voices Help Genie picks for common use cases.
- **All** — the complete library with filters for gender, accent, age, and use case.
- **Favourites** — voices you have starred for later.
- **Recent** — voices you have previewed lately.
- **Collections** — themed sets (e.g. warm support voices, energetic sales voices).

## How to use it well

- Hit play on a voice to hear a sample.
- Click the speech-bubble icon to try a custom phrase — ideal for testing how a voice says your product names.
- Star anything you like to find it again under **Favourites**.
- Click **Create a Voice** in the top right if you want to clone or describe a custom voice from scratch.

## Assigning a voice to a Genie

Once you have a voice you like, open the Genie's detail page in **My Genies**, find the voice section, and pick the voice. The Genie's next response will use it.`,
  },
  {
    id: "static-pronunciations",
    slug: "add-custom-pronunciations",
    title: "Add custom pronunciations",
    category: "genies",
    summary: "Teach your Genie how to say product names, founder names, and unusual words.",
    video_url: null,
    est_minutes: 3,
    order_index: 9,
    is_start_here: false,
    body_markdown: `## Why this matters

Voice Genies need to say product names, brand names, and unusual words correctly. Out of the box, voices will guess — sometimes hilariously, sometimes embarrassingly. Custom pronunciations override that.

## Where this lives

Open the Genie's detail page and find the **Pronunciations** section. Add an entry per word: the word as it appears in text, and the phonetic spelling you want it to say.

## How to phrase the pronunciation

Spell it the way you'd type it for a friend to read aloud. "Genie" might be "JEE-nee". "Acme" might be "ACK-mee". You don't need IPA — natural phonetic spelling works.

## Test after each change

Open the Voice Playground or the live Genie page and ask it a question that includes the word. Listen. Adjust the spelling until it sounds right.

## Common candidates

- Your brand and product names.
- Founder or staff names that appear in conversations.
- Industry jargon and acronyms (e.g., "SaaS" should be "sass", not S-A-A-S).
- Suburb, region, or city names with unusual pronunciations.`,
  },
  {
    id: "static-multilingual",
    slug: "make-your-genie-multilingual",
    title: "Make your Genie multilingual",
    category: "genies",
    summary: "Let your Genie switch language mid-conversation based on what the caller speaks.",
    video_url: null,
    est_minutes: 3,
    order_index: 10,
    is_start_here: false,
    body_markdown: `## What multilingual unlocks

A multilingual Genie can switch language mid-conversation based on what the user says. Useful for support lines that serve multiple language communities or for international product Genies.

## Turning it on

Open the Genie's detail page from **My Genies**. In the configuration section, toggle **Multilingual**. The Genie will respond in whichever supported language the caller uses.

## What changes

- The voice model switches to a multilingual TTS engine.
- The Genie listens for language hints in the first turn and follows the caller's lead.
- Knowledge base content is used as-is — the Genie translates on the fly when asked in another language.

## Best practice

- Pick a voice that has strong multilingual range. The Voice Playground notes which voices support which languages.
- Add a short note in the system prompt: "If the user speaks Spanish, continue in Spanish for the rest of the conversation."
- Test each language you expect by asking the Genie a real question in the Voice Playground.

## When not to use it

If you only serve one language, leave it off. A single-language Genie produces slightly higher-quality voice output and a more consistent feel.`,
  },
  {
    id: "static-system-prompt",
    slug: "write-a-system-prompt",
    title: "Write a system prompt that works",
    category: "genies",
    summary: "Four ingredients of a clear, effective Genie brief.",
    video_url: null,
    est_minutes: 4,
    order_index: 11,
    is_start_here: false,
    body_markdown: `## What a system prompt does

The system prompt is the brief you give your Genie. It sets the voice, the boundaries, and how to respond when it doesn't know something. A vague prompt produces a vague Genie.

## The four parts of a good prompt

1. **Identity** — who the Genie is. "You are the front-of-house Genie for Acme Plumbing."
2. **Purpose** — what it's there to do. "Your job is to take after-hours service requests and book callbacks."
3. **Tone** — how it should feel. "Speak warmly and confidently. Use plain English, never jargon."
4. **Rules** — what it must and must not do. "Always confirm the address before ending. Never quote prices."

## Where to edit it

Open the Genie's detail page from **My Genies**, find the **Prompt** card, and edit. Save and the Genie picks up the change immediately — no redeploy required.

## Iterating

Test in the Voice Playground after every change. Read five real conversations from the past week. Where did the Genie go off-script? Add a line to the prompt to handle it. This compounding loop is how you go from "OK" to "great" in a couple of weeks.

## What not to do

Don't paste a wall of FAQs into the prompt. Use the **Knowledge Base** for facts. Use the prompt for personality, behaviour, and rules.`,
  },
  {
    id: "static-lead-capture",
    slug: "set-up-lead-capture",
    title: "Set up lead capture on your Genie",
    category: "genies",
    summary: "Decide what your Genie asks for — and where those leads go.",
    video_url: null,
    est_minutes: 3,
    order_index: 12,
    is_start_here: false,
    body_markdown: `## How lead capture works

When lead capture is on, your Genie collects contact details during the conversation and stores them on your **Leads** page. You decide what to capture and when.

## Turning it on

Open the Genie's detail page from **My Genies** and find the **Lead Capture** section. Toggle it on. Configure which fields you want collected.

## What to capture

Less is more. Each extra field cuts conversion. Sensible defaults:

- **Name** — almost always worth asking.
- **Email** — high-value, low-friction.
- **Phone** — ask only when there's a clear payoff (callback, demo, urgent service).
- **Custom field** — one or two at most. Use dropdowns when you can.

## Where leads go

Captured leads appear on the **Leads** page in your sidebar. You can also route them to a CRM, an email inbox, or a Slack channel via **Connections**. Set up at least one external destination so leads never live in only one place.

## Common patterns

- **Service business** — name, phone, suburb, brief description of the job.
- **B2B SaaS** — name, work email, company, role.
- **E-commerce** — name, email, the product they were asking about.`,
  },
  {
    id: "static-brand",
    slug: "brand-your-genie",
    title: "Brand your Genie",
    category: "genies",
    summary: "Make every Genie surface look and feel like your company.",
    video_url: null,
    est_minutes: 3,
    order_index: 13,
    is_start_here: false,
    body_markdown: `## Two levels of branding

- **Account branding** sets the defaults applied to every new Genie.
- **Per-Genie branding** overrides those defaults for a single Genie.

## Setting account-wide branding

Open **Account Branding** from the sidebar (under System & Settings). You can set:

- **Primary colour** — used for buttons, accents, and the widget bubble.
- **Logo** — appears on Portal pages, embed widgets, and PDF reports.
- **Gradient toggle** — turn the brand gradient on or off.

Anything you set here flows into every Genie you create from now on. Existing Genies are not changed retroactively unless you re-sync them.

## Overriding for one Genie

Open the Genie's detail page in **My Genies**, find the **Branding** section, and adjust the colours, logo, and gradient just for that Genie.

## Where branding shows up

Every public-facing surface uses the brand: Portal pages, embed widgets, PDF reports, the QR code, and even your Help Genie email templates.`,
  },
  {
    id: "static-portal",
    slug: "publish-via-a-portal",
    title: "Publish your Genie via a Portal",
    category: "genies",
    summary: "Share your Genie with a branded link or embed it on your site.",
    video_url: null,
    est_minutes: 3,
    order_index: 14,
    is_start_here: true,
    body_markdown: `## What a Portal is

A **Portal** is a public, branded page that hosts one or more of your Genies. Each Portal has its own URL and welcome message, and inherits your account brand by default.

## Creating one

Open **Portals** in the sidebar and click **Create Portal**. Fill in:

- **Name** — what visitors see at the top of the page.
- **Slug** — the URL path (e.g. \`acme-support\`).
- **Description** — a short intro for the page.

You then pick which Genies appear on the Portal and save. The shareable link is generated instantly.

## Embedding the widget on your own site

From the Portal detail page you can grab an embed snippet. Drop it into your site and the Genie appears as a floating widget — same look and feel as the Portal page.

## Branding the Portal

The Portal uses your account branding out of the box. To override colours, hero image, or welcome message for that specific Portal, open the Portal's edit panel.`,
  },
  {
    id: "static-customise-portal",
    slug: "customise-your-portal",
    title: "Customise your Portal page",
    category: "genies",
    summary: "Override branding, the welcome message, and which Genies appear on a Portal.",
    video_url: null,
    est_minutes: 3,
    order_index: 15,
    is_start_here: false,
    body_markdown: `## What you can customise

A Portal inherits your account branding by default, but you can override almost everything for that specific Portal. Open the Portal from **Portals** in the sidebar, then click into its detail page.

## The main controls

- **Name and slug** — the title shown to visitors and the URL path.
- **Welcome message** — the text greeting at the top of the page.
- **Hero image** — a banner image that sets the visual tone.
- **Primary colour** — buttons, accents, and the embedded widget bubble.
- **Logo** — overrides your account-wide logo for this Portal only.
- **Genies** — which of your Genies appear on the page, and in what order.
- **Privacy** — public, password-protected, or restricted to specific domains.

## Embedding the widget

From the Portal detail page, copy the embed snippet and paste it into your site's HTML. The widget appears as a floating chat bubble using the Portal's branding.

## When to use multiple Portals

Run two if you have two distinct brands or audiences. A common pattern is one Portal for support (sober tone, support Genie) and one for sales (energetic, sales Genie). Each gets its own URL and branding.`,
  },
  {
    id: "static-phone",
    slug: "connect-a-phone-number",
    title: "Give your Genie a phone number",
    category: "integrations",
    summary: "Take real inbound calls with your Genie.",
    video_url: null,
    est_minutes: 4,
    order_index: 16,
    is_start_here: false,
    body_markdown: `## Where this lives

Phone numbers are attached to individual Genies, not the whole account. Open the Genie you want to give a number to from **My Genies**, then open its **Distribution** section.

## Two options

- **Provision a new number** — pick a region and choose a number from the available list. Help Genie sets it up and links it to the Genie.
- **Transfer an existing number** — bring a number you already own.

Either flow opens the **Phone Number** modal on the Genie detail page.

## After-hours pattern

A common setup is: route after-hours calls to your Genie, and business-hours calls to your team. You configure that on the number itself after attaching it.

## Billing

Numbers and call minutes are billed separately from your subscription. Check **Account Settings → Subscription** to see current usage and rates. Cancelling a number releases it permanently.

## Other channels

Phone is one channel. To set up Slack, Teams, Telegram, WhatsApp, email, or webhook notifications, head to **Connections** in the sidebar — that is a separate area, not where phone numbers live.`,
  },
  {
    id: "static-slack-teams",
    slug: "connect-slack-or-teams",
    title: "Connect Slack or Teams to your Genie",
    category: "integrations",
    summary: "Get lead and conversation alerts in the channel your team already lives in.",
    video_url: null,
    est_minutes: 3,
    order_index: 17,
    is_start_here: false,
    body_markdown: `## What this unlocks

When you connect Slack or Microsoft Teams, your Genie can post notifications to a channel — new leads, conversation summaries, escalations — without anyone watching a separate dashboard.

## Adding the connection

Open **Connections** in the sidebar. You'll see the available platforms. Click **Slack** (or **Teams**) and walk through the OAuth flow. You authenticate with your workspace, pick which channel notifications should land in, and confirm.

## What gets sent

By default, your Genies will post:

- **New lead captured** — name, contact details, and the question that triggered the capture.
- **Conversation flagged** — when a conversation ends with frustration or an unresolved request.

You can customise which events trigger a post in the connection settings.

## Per-Genie routing

Each Genie can route to a different Slack channel. Open the Genie's detail page, find the notification settings, and pick where its alerts should go.

## Disconnecting

Open **Connections**, find the active integration, and click **Remove**. Your Genies stop posting immediately and the OAuth tokens are revoked.`,
  },
  {
    id: "static-email",
    slug: "set-up-email-notifications",
    title: "Set up email notifications",
    category: "integrations",
    summary: "Send branded email alerts when your Genie captures leads or finishes calls.",
    video_url: null,
    est_minutes: 3,
    order_index: 18,
    is_start_here: false,
    body_markdown: `## What you get

Your Genie can email you (or your team) when it captures a lead, finishes a conversation, or hits a configured trigger. Helpful for teams without Slack or for high-priority alerts.

## Configuring the recipients

Open the Genie's detail page from **My Genies**. Find the **Email** or **Notifications** section. Add one or more recipient email addresses. Toggle which events trigger an email.

## What emails look like

Each email arrives branded with your account branding (or per-Genie branding if you've set that) and includes the lead details, the conversation summary, and a link straight to the conversation in Help Genie.

## Customising the template

Open **Email Templates** in the sidebar (under System & Settings). You can edit the layout, copy, and styling of the emails your Genie sends. Changes apply to all future emails — past notifications keep their original look.

## Stopping emails

Toggle the event off on the Genie's notification settings, or remove the recipient address. The change takes effect immediately.`,
  },
  {
    id: "static-insights",
    slug: "read-your-conversations-and-insights",
    title: "Read your conversations + insights",
    category: "analytics",
    summary: "Where to find every conversation and what to look for.",
    video_url: null,
    est_minutes: 4,
    order_index: 19,
    is_start_here: true,
    body_markdown: `## Conversations

Open **Conversations** in the sidebar. Every chat and call your Genies have handled is listed there. You can filter by:

- **Genie** — pick one to focus on a single assistant.
- **Time range** — 7 days, 30 days, or custom windows.

Click any row to open the full transcript. For voice calls you can replay the audio. You can also mark conversations as reviewed once you have read them.

## Insights

The **Insights** page sits next to Conversations. It surfaces what is happening across many conversations at once:

- KPI summary — total conversations, how many had analysis, how many produced leads, this week's volume.
- Per-conversation AI analysis — sentiment, topics raised, lead status.
- Filters by Genie and date range, plus an Excel export.

## What to look for weekly

- **Drop-offs** — where conversations end abruptly. Often a sign the Genie could not answer.
- **Repeated questions** — if many users ask the same thing, add it to the Knowledge Base.
- **Lead quality** — are captured leads complete enough to act on? Tighten the lead form if not.
- **Sentiment trend** — sliding negative? Time to revisit the prompt or knowledge.

## Analytics for numbers

For volume and trend charts — conversations per day, average length, completion rate — open the **Analytics** page (also in the sidebar under Genie Intelligence).`,
  },
  {
    id: "static-leads",
    slug: "find-and-manage-your-leads",
    title: "Find and manage your leads",
    category: "analytics",
    summary: "Filter, sort, update, and export every lead your Genies have captured.",
    video_url: null,
    est_minutes: 3,
    order_index: 20,
    is_start_here: false,
    body_markdown: `## Where leads live

Every lead your Genies capture lands on the **Leads** page in the sidebar. Each entry includes the contact details, the Genie that captured it, the conversation it came from, and a status.

## Filtering and sorting

The Leads page supports:

- **Search** — by name, email, phone, or any captured field.
- **Genie filter** — focus on leads from a specific Genie.
- **Status filter** — new, contacted, qualified, won, lost.
- **Date range** — last 7 days, last 30, custom.

## Updating a lead's status

Click any lead to open its detail drawer. Update status, add internal notes, view the original conversation transcript, and assign it to a team member if you have shared seats.

## Exporting

The Leads page has an export button — pulls the current filtered view as a CSV. Useful for handing leads to a CRM that doesn't have a direct integration.

## Best practice

- Set a status convention with your team (New → Contacted → Qualified → Won / Lost).
- Review **New** leads at least once a working day.
- Use notes to record what you tried — voicemails, callbacks, emails — so the next person picking it up has context.`,
  },
  {
    id: "static-weekly-review",
    slug: "your-weekly-genie-review",
    title: "Your weekly Genie review",
    category: "analytics",
    summary: "A 20-minute weekly habit that compounds your Genie's quality over time.",
    video_url: null,
    est_minutes: 4,
    order_index: 21,
    is_start_here: false,
    body_markdown: `## Why this matters

Genies get better when you look at what they actually said. A short weekly review compounds: spot one issue, fix one thing in the prompt or knowledge, and the next week's conversations are cleaner.

## The 20-minute weekly habit

Pick the same time each week. Block 20 minutes. Open **Conversations** in the sidebar and set the time range to the last 7 days.

### 1. Read five real chats (10 min)

Pick five at random, especially ones flagged as low-sentiment or longer than usual. Read end to end. Look for:

- Moments where the Genie clearly didn't know something.
- Repeated phrasing the Genie missed.
- Anything that sounded off-brand.

### 2. Skim the Insights page (5 min)

Open **Insights** and scan the top themes. Anything trending up that surprised you? Anything trending down you expected to grow?

### 3. Take one action (5 min)

Fix exactly one thing:

- Add a knowledge document, or update an existing one.
- Tweak the system prompt to handle a missed case.
- Update a pronunciation.
- Adjust the lead capture fields.

One change per week is enough. The point is the loop, not the volume.

## After eight weeks

You'll have a Genie that is noticeably better than the one you launched with — without ever doing a "big revamp". This is how every great Genie gets there.`,
  },
];

export const LEARN_ARTICLES_BY_SLUG: Record<string, LearnArticle> = Object.fromEntries(
  LEARN_ARTICLE_REGISTRY.map((a) => [a.slug, a]),
);

export function getStaticArticles(filters?: {
  category?: string;
  searchTerm?: string;
  startHere?: boolean;
}): LearnArticle[] {
  let list = [...LEARN_ARTICLE_REGISTRY];
  if (filters?.startHere) list = list.filter((a) => a.is_start_here);
  if (filters?.category) list = list.filter((a) => a.category === filters.category);
  if (filters?.searchTerm) {
    const t = filters.searchTerm.toLowerCase();
    list = list.filter(
      (a) =>
        a.title.toLowerCase().includes(t) ||
        (a.summary ?? "").toLowerCase().includes(t) ||
        (a.body_markdown ?? "").toLowerCase().includes(t),
    );
  }
  return list.sort((a, b) => a.order_index - b.order_index);
}

export function getStaticArticleBySlug(slug: string): LearnArticle | null {
  return LEARN_ARTICLES_BY_SLUG[slug] ?? null;
}
